import { useState } from "react";
import axios from "axios";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const NAV_H = 72;

export const NAV_LINKS = [
  { id: "home", label: "Home" },
  { id: "how-it-works", label: "How It Works" },
  { id: "features", label: "Features" },
  { id: "launching-soon", label: "Launching Soon" },
  { id: "pricing", label: "Pricing" },
];

export const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export const pinnedHeight = () => document.querySelector('[data-testid="site-nav"]')?.offsetHeight ?? NAV_H;

const docTop = (el) => el.getBoundingClientRect().top + window.scrollY;
const maxScroll = () => document.documentElement.scrollHeight - window.innerHeight;

function smoothTo(getTop, onDone) {
  const reduced = prefersReducedMotion();
  const target = () => Math.max(0, Math.min(getTop(), maxScroll()));
  window.scrollTo({ top: target(), behavior: reduced ? "auto" : "smooth" });
  const start = performance.now();
  let last = -1;
  let still = 0;
  const tick = () => {
    const y = window.scrollY;
    still = Math.abs(y - last) < 1 ? still + 1 : 0;
    last = y;
    const elapsed = performance.now() - start;
    if ((still < 8 || elapsed < 250) && elapsed < 3000) return requestAnimationFrame(tick);
    const t = target();
    if (Math.abs(window.scrollY - t) > 3) window.scrollTo({ top: t, behavior: "auto" });
    onDone?.();
  };
  requestAnimationFrame(tick);
}

const waitlistTop = () => {
  const section = document.getElementById("launching-soon");
  const card = document.getElementById("waitlist-card");
  let top = docTop(section) - pinnedHeight() + 1;
  if (card) {
    const cardTop = docTop(card);
    top = Math.max(top, cardTop + card.offsetHeight + 40 - window.innerHeight);
    top = Math.min(top, cardTop - pinnedHeight() - 16);
  }
  return top;
};

export function scrollToWaitlist() {
  if (!document.getElementById("launching-soon")) return;
  window.history.replaceState(null, "", "#launching-soon");
  smoothTo(waitlistTop, () => document.getElementById("wl-name")?.focus({ preventScroll: true }));
}

export function scrollToId(id) {
  if (id === "launching-soon") return scrollToWaitlist();
  const el = document.getElementById(id);
  if (!el) return;
  window.history.replaceState(null, "", `#${id}`);
  smoothTo(() => (id === "home" ? 0 : docTop(el) - pinnedHeight() + 1));
}

export const goToWaitlist = (e) => {
  e?.preventDefault();
  scrollToWaitlist();
};

export const validators = {
  name: (v) => (v.trim().length >= 2 ? "" : "Please enter your full name (at least 2 characters)."),
  email: (v) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v.trim()) ? "" : "Please enter a valid email address.",
  phone: (v) => {
    const t = v.trim();
    if (!t) return "Please enter your phone number.";
    if (!/^\+?[\d\s\-().]+$/.test(t)) return "Use digits only, with an optional leading + for the country code.";
    const d = t.replace(/\D/g, "").length;
    return d >= 7 && d <= 15 ? "" : "Phone number should contain 7 to 15 digits.";
  },
  message: (v) => (v.trim().length >= 10 ? "" : "Please write a message of at least 10 characters."),
};

export function useSiteForm({ initial, rules, endpoint }) {
  const [values, setValues] = useState(initial);
  const [honeypot, setHoneypot] = useState("");
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const [formError, setFormError] = useState("");
  const [result, setResult] = useState(null);

  const onChange = (e) => {
    const { name, value } = e.target;
    setValues((v) => ({ ...v, [name]: value }));
    if (errors[name]) setErrors(({ [name]: _, ...rest }) => rest);
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = Object.fromEntries(
      rules.map((k) => [k, validators[k](values[k] ?? "")]).filter(([, m]) => m)
    );
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setStatus("loading");
    setFormError("");
    try {
      const { data } = await axios.post(`${API}${endpoint}`, { ...values, website: honeypot });
      setResult(data);
      setStatus("success");
    } catch (err) {
      const r = err.response;
      if (r?.status === 422 && r.data?.errors && Object.keys(r.data.errors).length) {
        setErrors(r.data.errors);
        setStatus("idle");
        return;
      }
      setFormError(
        r?.status === 429
          ? "You've made several attempts in a short time. Please wait a minute and try again."
          : "We couldn't send that just now. Please check your connection and try again."
      );
      setStatus("error");
    }
  };

  return { values, errors, status, formError, result, onChange, submit, honeypot, setHoneypot };
}
